import React, { useEffect } from "react";
import styles from "../EditBase.module.scss";
import { useNavigate } from "react-router";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import EditProfile from "./EditProfile";
import EditPlayerHistories from "./EditPlayerHistories";
import EditSNS from "./EditSNS";
import { ActivityHistoryData } from "../../../../common/classes/ActivityHistoryData";
import { SonicDBEntry } from "../../../../common/classes/SonicDBEntry";
import { SonicLinkedPagesData } from "../../../../common/classes/SonicLinkedPages";
import Form from "../../../../common/forms/Form";
import BackBar from "../../../../common/nav/BackBar";
import SaveButton from "../../../../common/nav/SaveButton";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";

import SelenaA from "../../../../assets/img/service/selena-a.webp";
import EditWork from "./EditWork";
import { SonicWorkInputData } from "./SonicWorkInputData";

import { RequestState } from "../../../../utils/RequestState";
import { SonicTermData } from "../../../../common/classes/SonicTerm";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import { SonicGameDetailData } from "../../../../common/classes/SonicGameDetail";
import { SonicGameSelectionData } from "../../../../common/classes/SonicGameSelection";

type Props = {
  error: boolean;
  handleSubmit?: () => void;
  handleSave?: () => void;
  handleInputChange: (value: File | string | undefined, name: string) => void;
  isHasChanges: boolean;
  requestState: RequestState;

  database_entry?: SonicDBEntry;
  linked_pages?: SonicLinkedPagesData | undefined;
  editImg?: string;
  typeOptions?: Array<[string, string]>;

  // MyGame
  games?: Array<SonicTermData>;
  handleGames: (value: Array<SonicTermData>) => void;

  // Team
  team?: SonicTermData;
  handleTeam: (value: SonicTermData) => void;

  // player history
  historyItems: Array<ActivityHistoryData>;
  addHistoryInput: () => void;
  deleteHistory: (i: number) => void;
  handleHistoryInputChange: (value: string | undefined, name: string) => void;

  // works
  workItems?: Array<SonicWorkInputData>;
  isEditWork: boolean;
  editWorkNumber: number;
  addWork: () => void;
  deleteWork: (i: number) => void;
  editWork: (i: number, work: SonicWorkInputData) => void;
  setIsEditWork: (value: boolean) => void;
  setEditWorkNumber: (i: number) => void;

  // game details
  gameDetailLoading: boolean;
  gameDetailOptions?: Array<SonicGameSelectionData>;
  handleGameDetails: (details: Array<SonicGameDetailData>) => void;
};

const EditDatabaseEntry: React.FC<Props> = ({
  error,
  handleSubmit,
  handleSave,
  handleInputChange,
  isHasChanges,
  requestState,
  database_entry,
  editImg,
  typeOptions,
  games,
  handleGames,
  team,
  handleTeam,
  historyItems,
  addHistoryInput,
  deleteHistory,
  handleHistoryInputChange,
  workItems,
  isEditWork,
  editWorkNumber,
  addWork,
  deleteWork,
  editWork,
  setIsEditWork,
  setEditWorkNumber,
  gameDetailLoading,
  gameDetailOptions,
  handleGameDetails,
}) => {
  const navigate = useNavigate();

  const genNotificationColor = () => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return "#fe493d";
      case RequestState.Doing:
        return "#f79b00";
      case RequestState.Success:
        return "#27c900";
      default:
        return "";
    }
  };

  const genNotificationMessage = () => {
    switch (requestState) {
      case RequestState.Pending:
        return "保存していない変更があります";
      case RequestState.Doing:
        return "通信中です";
      case RequestState.Success:
        return "保存が完了しました";
      case RequestState.Failed:
        return "保存に失敗しました";
      default:
        return "";
    }
  };

  const genNotificationIcon = (): IconProp | undefined => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return ["fal", "exclamation-circle"];
      case RequestState.Success:
        return ["fal", "check-circle"];
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (requestState === RequestState.Doing) {
      window.scrollTo(0, 0);
    }
  }, [requestState]);

  return (
    <>
      {requestState !== RequestState.Doing ? (
        <>
          <BackBar
            title={database_entry ? "プロフィール編集" : "プロフィール作成"}
            editable={true}
            handleClose={() => navigate(-1)}
            submitHandler={handleSubmit}
            saveHandler={handleSave}
            hideSubmitButton={
              (!database_entry && !isHasChanges) ||
              (database_entry && isHasChanges)
                ? true
                : false
            }
            hideSaveButton={!isHasChanges ? true : false}
          />
          <Form handler={() => {}}>
            <EditProfile
              handleInputChange={handleInputChange}
              img_src={editImg}
              name={database_entry?.name}
              kana={database_entry?.kana}
              player_type={
                database_entry?.player_type?.length
                  ? database_entry.player_type[0].id
                  : ""
              }
              profile={database_entry?.profile}
              birthplace={database_entry?.birthplace}
              weight={database_entry?.weight}
              height={database_entry?.height}
              hobby={database_entry?.hobby}
              typeOptions={typeOptions}
              games={games}
              handleGames={handleGames}
              team={team}
              handleTeam={handleTeam}
              gameDetails={database_entry?.game_details}
              gameDetailLoading={gameDetailLoading}
              gameDetailOptions={gameDetailOptions}
              handleGameDetails={handleGameDetails}
            />
            <EditPlayerHistories
              historyItems={historyItems}
              addHistoryInput={addHistoryInput}
              deleteHistory={deleteHistory}
              handleHistoryInputChange={handleHistoryInputChange}
            />
            <EditSNS
              handleInputChange={handleInputChange}
              twitter={database_entry?.sns?.twitter}
              twitch={database_entry?.sns?.twitch}
              instagram={database_entry?.sns?.instagram}
              facebook={database_entry?.sns?.facebook}
              youtube={database_entry?.sns?.youtube}
            />
            <EditWork
              works={workItems}
              isEditWork={isEditWork}
              editWorkNumber={editWorkNumber}
              addWork={addWork}
              editWork={editWork}
              setIsEditWork={setIsEditWork}
              setEditWorkNumber={setEditWorkNumber}
              deleteWork={deleteWork}
            />
          </Form>

          {error ? (
            <div className={styles["p-edit__error"]}>
              必須項目が未入力、または入力内容に誤りがあります
            </div>
          ) : null}

          {handleSave ? <SaveButton title="保存" onClick={handleSave} /> : null}

          {(handleSubmit && !isHasChanges) || !handleSave ? (
            <SaveButton title="公開申請" onClick={handleSubmit} />
          ) : null}
        </>
      ) : (
        <LoadingSpinner space={1} />
      )}
      {isHasChanges || requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor()}
          icon_class={genNotificationIcon()}
          message={genNotificationMessage()}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </>
  );
};

export default EditDatabaseEntry;
